import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useGetAllAttendQuery } from '@/store/attendenceApi';
import { useSelector } from 'react-redux';
import { RootState } from '@/types';

export default function AttendanceBar() {
  const { user } = useSelector((state: RootState) => state.user);
  const { data: attendance = [] } = useGetAllAttendQuery(user?.token);

  // Process the attendance data
  const attendanceData = React.useMemo(() => {
    // Helper to get the start and end dates of the current week
    const getWeekBounds = () => {
      const today = new Date();
      const dayOfWeek = today.getDay(); // Sunday is 0
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - dayOfWeek + 1); // Adjust to Monday
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + (7 - dayOfWeek)); // Adjust to Sunday
      endOfWeek.setHours(23, 59, 59, 999);

      return { startOfWeek, endOfWeek };
    };

    const { startOfWeek, endOfWeek } = getWeekBounds();

    // Filter attendance data for the current week
    const weeklyData = attendance.filter((record) => {
      const recordDate = new Date(record.date);
      return recordDate >= startOfWeek && recordDate <= endOfWeek;
    });

    // Initialize day-based attendance summary
    const dayAttendance = {
      Mon: { totalPresent: 0, totalAbsent: 0 },
      Tue: { totalPresent: 0, totalAbsent: 0 },
      Wed: { totalPresent: 0, totalAbsent: 0 },
      Thu: { totalPresent: 0, totalAbsent: 0 },
      Fri: { totalPresent: 0, totalAbsent: 0 },
      Sat: { totalPresent: 0, totalAbsent: 0 },
      Sun: { totalPresent: 0, totalAbsent: 0 },
    };

    // Process weekly data
    weeklyData.forEach((record) => {
      const day = new Date(record.date).toLocaleDateString('en-US', {
        weekday: 'short',
      }); // e.g., "Mon"
      if (dayAttendance[day]) {
        if (record.attendence) {
          dayAttendance[day].totalPresent += 1;
        } else {
          dayAttendance[day].totalAbsent += 1;
        }
      }
    });

    // Convert to array format
    return Object.entries(dayAttendance).map(([day, data]) => ({
      day,
      ...data,
    }));
  }, [attendance]);

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <CardTitle>
          <h1>Attendance</h1>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={attendanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="totalPresent" fill="#127971" />
              <Bar dataKey="totalAbsent" fill="#49ccc1e2" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
